import { useCallback } from "react";

import { buildImageUploadDedupeKey, enqueueImageUpload } from "@/common/helpers/imageUploadQueue";
import { trpc } from "@/common/helpers/trpc";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

const RATE_PHOTO_UPLOAD_PRESET = "square_800";

interface UploadRemainingRatePhotosParams {
  rates: Pick<RateData, "photos">[] | undefined;
  setPhotoUrl: (rateIndex: number, photoIndex: number, photoUrl: string | null) => void;
}

export const useUploadRemainingRatePhotos = () => {
  const { mutateAsync: uploadImage } = trpc.uploadImage.useMutation();

  return useCallback(
    async ({ rates, setPhotoUrl }: UploadRemainingRatePhotosParams): Promise<void> => {
      if (!rates?.length) {
        return;
      }

      await Promise.all(
        rates.flatMap((rate, rateIndex) =>
          (rate.photos ?? []).map(async (photo, photoIndex) => {
            const photoUrl = photo?.photoUrl;
            if (!photoUrl) {
              return;
            }

            if (photoUrl.startsWith("blob:")) {
              setPhotoUrl(rateIndex, photoIndex, null);
              return;
            }

            if (!photoUrl.startsWith("data:")) {
              return;
            }

            const { data } = await enqueueImageUpload({
              dedupeKey: buildImageUploadDedupeKey({ file: photoUrl, uploadPreset: RATE_PHOTO_UPLOAD_PRESET }),
              upload: () => uploadImage({ file: photoUrl, uploadPreset: RATE_PHOTO_UPLOAD_PRESET }),
            }).catch(() => ({ data: null }));
            setPhotoUrl(rateIndex, photoIndex, data?.url ?? null);
          })
        )
      );
    },
    [uploadImage]
  );
};
